import { ref } from 'vue'
import { isOssUrl, ossExpiresAt, ossObjectPath } from './oss'

type Source = {
  url: string
  previewUrl?: string
  source: string
  reload: () => Promise<unknown>
  current: () => boolean
}
export type OssImageVariant = 'thumbnail' | 'preview'
const images = new Map<string, Source>()
const previews = new Map<string, string>()
const pending = new Map<string, Promise<unknown>>()
const retryAfter = new Map<string, number>()
export const ossImageRevision = ref(0)
let session = ''

export const setOssSession = (token: string) => {
  if (session === token) return
  session = token
  images.clear()
  pending.clear()
  retryAfter.clear()
  for (const url of previews.values()) URL.revokeObjectURL(url)
  previews.clear()
  ossImageRevision.value++
  sessionStorage.removeItem('oss_sts_token')
}

export const registerOssPreview = (path: string, url: string) => {
  previews.set(ossObjectPath(path), url)
  ossImageRevision.value++
}
export const releaseOssPreview = (path: string, url: string) => {
  if (previews.get(ossObjectPath(path)) === url) previews.delete(ossObjectPath(path))
  URL.revokeObjectURL(url)
  ossImageRevision.value++
}

function* signedUrls(value: unknown): Generator<string> {
  if (typeof value === 'string') {
    if (ossExpiresAt(value)) yield value
    else if (/^\s*[\[{]/.test(value)) {
      try {
        yield* signedUrls(JSON.parse(value))
      } catch {
        /* 普通文本无需解析 */
      }
    } else if (/<(?:img|video|source)\b|&lt;(?:img|video|source)\b/i.test(value)) {
      // 历史 HTML 正文的媒体也沿用原请求的签名刷新与大图映射。
      const parser = new DOMParser()
      const html = /<(?:img|video|source)\b/i.test(value)
        ? value
        : parser.parseFromString(value, 'text/html').body.textContent || ''
      const document = parser.parseFromString(html, 'text/html')
      for (const node of Array.from(
        document.querySelectorAll('img[src], video[src], source[src]')
      )) {
        const url = node.getAttribute('src') || ''
        if (ossExpiresAt(url)) yield url
      }
    }
  } else if (Array.isArray(value)) {
    for (const item of value) yield* signedUrls(item)
  } else if (value && typeof value === 'object') {
    for (const item of Object.values(value)) yield* signedUrls(item)
  }
}

export const rememberOssImages = (
  data: unknown,
  source: string,
  reload: Source['reload'],
  current: Source['current'],
  previewUrls: Record<string, string> = {}
) => {
  if (!current()) return
  let changed = false
  for (const url of signedUrls(data)) {
    const key = ossObjectPath(url)
    const previewUrl = previewUrls[key]
    changed ||= images.get(key)?.url !== url || images.get(key)?.previewUrl !== previewUrl
    images.delete(key)
    images.set(key, { url, previewUrl, source, reload, current })
  }
  // 只缓存图片地址和来源，不持有业务表单；超出上限后由页面主动刷新。
  while (images.size > 1000) images.delete(images.keys().next().value!)
  for (const key of retryAfter.keys()) {
    if (![...images.values()].some((image) => image.source === key)) retryAfter.delete(key)
  }
  if (changed) ossImageRevision.value++
}

const usable = (url: string) => ossExpiresAt(url) > Date.now() + 30_000

export const resolveOssImage = async (
  value: string,
  variant: OssImageVariant = 'thumbnail'
): Promise<string> => {
  if (!value) return ''
  if (/^(blob:|data:image\/)/i.test(value)) return value
  const own = isOssUrl(value)
  if (/^https?:\/\//i.test(value) && !own) return value
  const key = ossObjectPath(value)
  const preview = previews.get(key)
  if (preview && !ossExpiresAt(value)) return preview
  const image = images.get(key)
  if (image && !image.current()) throw new Error('登录状态已变更，请重新加载页面')
  const selected = variant === 'preview' ? image?.previewUrl : image?.url
  if (selected && usable(selected)) return selected
  // 大图必须使用 Server 单独签名的地址，不能替换缩略图后缀或退回原图。
  if (variant === 'thumbnail' && own && usable(value)) return value
  if (!image) throw new Error('缺少有效图片地址，请刷新业务页面')
  let task = pending.get(image.source)
  if (!task) {
    if ((retryAfter.get(image.source) || 0) > Date.now())
      throw new Error('图片刷新失败，请稍后重试')
    retryAfter.set(image.source, Date.now() + 30_000)
    task = image.reload()
    pending.set(image.source, task)
  }
  try {
    await task
    const updated = images.get(key)
    const updatedUrl = variant === 'preview' ? updated?.previewUrl : updated?.url
    if (!image.current() || !updated?.current() || !updatedUrl || !usable(updatedUrl)) {
      throw new Error('业务接口未返回有效图片地址')
    }
    return updatedUrl
  } finally {
    if (pending.get(image.source) === task) pending.delete(image.source)
  }
}
